/* eslint-disable i18next/no-literal-string */
import {Button} from '@dropbox/dig-components/buttons';
import {SystemBanner} from '@dropbox/dig-components/system_banner';
import {Table} from '@dropbox/dig-components/table';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {useMutation} from '@tanstack/react-query';
import {Title} from 'components/DSYS/Title';

import {postAdminJson} from './postAdminJson';

type ModelProbeItem = {
  model: string;
  okapi_model: string;
  ok: boolean;
  latency_ms: number;
  error: string | null;
};

const ModelProbeTable = ({results}: {results: ModelProbeItem[]}) => (
  <Table>
    <Table.Header>
      <Table.Row>
        <Table.HeaderCell>Model</Table.HeaderCell>
        <Table.HeaderCell>Status</Table.HeaderCell>
        <Table.HeaderCell>Latency</Table.HeaderCell>
        <Table.HeaderCell>Detail</Table.HeaderCell>
      </Table.Row>
    </Table.Header>
    <Table.Body>
      {results.map((r) => (
        <Table.Row key={r.model}>
          <Table.Cell>
            <Stack gap="4">
              <Text>{r.model}</Text>
              <Text size="small" color="subtle">
                {r.okapi_model}
              </Text>
            </Stack>
          </Table.Cell>
          <Table.Cell>
            <Text color={r.ok ? 'standard' : 'error'}>{r.ok ? 'ok' : '✗ failed'}</Text>
          </Table.Cell>
          <Table.Cell>{r.latency_ms.toLocaleString()}ms</Table.Cell>
          <Table.Cell>
            <Text size="small" color="subtle">
              {r.error}
            </Text>
          </Table.Cell>
        </Table.Row>
      ))}
    </Table.Body>
  </Table>
);

export const ModelProbePanel = () => {
  const probeMutation = useMutation({
    mutationFn: () => postAdminJson<{results: ModelProbeItem[]}>('/api/v1/ai/query/models/probe'),
  });
  const results = probeMutation.data?.results;

  return (
    <Box
      borderRadius="Large"
      borderWidth="1"
      borderStyle="Solid"
      borderColor="Border Subtle"
      padding="16"
      backgroundColor="Background Base"
    >
      <Stack gap="16">
        <Stack gap="8">
          <Title size={18}>Model reachability</Title>
          <Text color="subtle">
            Send each candidate model one tool-calling request with this environment&apos;s
            credentials. v2/ rows go through OKAPIv2 with the virtual key in
            &lt;env&gt;/pulse/okapiv2_vk; the rest go through OKAPI v1.
          </Text>
        </Stack>

        <Box>
          <Button
            variant="primary"
            onClick={() => probeMutation.mutate()}
            isLoading={probeMutation.isPending}
            disabled={probeMutation.isPending}
          >
            Probe models
          </Button>
        </Box>

        {results ? (
          <Stack gap="8" role="status">
            <Text isBold>
              {results.filter((r) => r.ok).length} of {results.length} models reachable
            </Text>
            <ModelProbeTable results={results} />
          </Stack>
        ) : null}

        {probeMutation.error instanceof Error ? (
          <SystemBanner tone="alert">
            <Text>{probeMutation.error.message || 'Unable to probe models.'}</Text>
          </SystemBanner>
        ) : null}
      </Stack>
    </Box>
  );
};
